import { ReactElement } from 'react'
import { Box, MenuItem, Select, Tooltip } from '@mui/material'
import useSelectTooltip from '../hooks/useSelectTooltip'
import { ChatModelOption } from '../modelAnimals'

interface Props {
  models: ChatModelOption[]
  value: string | undefined
  onChange: (model: string) => void
}

/**
 * The model of the next turn, from the API's catalogue (`AGENT_MODELS`).
 * Rendered only once the catalogue is loaded and the value is one of its
 * entries, so the select never shows an out-of-range value.
 */
const ModelSelect = ({ models, value, onChange }: Props): ReactElement | null => {
  const { tooltipProps, selectProps } = useSelectTooltip()

  if (!value || !models.some(model => model.id === value)) return null

  return (
    <Tooltip
      title="Modell für die nächste Antwort"
      {...tooltipProps}
      disableInteractive
      disableFocusListener
    >
      <Select
        {...selectProps}
        size="small"
        variant="standard"
        disableUnderline
        value={value}
        // The closed select shows the name only; icons belong to the list.
        renderValue={selected => models.find(model => model.id === selected)?.label}
        onChange={event => onChange(event.target.value)}
        inputProps={{ 'aria-label': 'Modell' }}
        sx={{ fontSize: 13, maxWidth: 180, '& .MuiSelect-select': { py: 0.25 } }}
        data-cy="ai-chat-model"
      >
        {models.map(model => (
          <MenuItem key={model.id} value={model.id} dense>
            {model.label}
            {model.icon && (
              <Box component="span" aria-hidden sx={{ ml: 0.75 }}>
                {model.icon}
              </Box>
            )}
          </MenuItem>
        ))}
      </Select>
    </Tooltip>
  )
}

export default ModelSelect
